import { useRef, useState } from 'react';
import { UploadCloud } from 'lucide-react';
import { UploadProgressBar } from '../ui/UploadProgress';

// Generic drag/drop + click-to-browse file picker used across the onboarding
// wizard's upload steps (CLE-131). Single-file; the parent owns what to do
// with the chosen File.

interface FileDropzoneProps {
  /** `accept` attribute for the hidden <input>, e.g. ".xlsx" or ".csv,.xlsx". */
  accept?: string;
  /** Short hint shown under the icon, e.g. "Only .xlsx". */
  hint?: string;
  disabled?: boolean;
  onFile: (file: File) => void;
  /**
   * The file currently being sent and validated, if any. While set, the zone
   * shows that file and a moving progress bar instead of the idle prompt —
   * these steps hand the whole file to the server and wait for it to parse,
   * which is a wait worth drawing rather than dimming the box and printing a
   * line of text somewhere below it.
   */
  busyFile?: File | null;
  /** What the server is doing, e.g. "Validating the file…". */
  busyLabel?: string;
}

export function FileDropzone({
  accept,
  hint,
  disabled,
  onFile,
  busyFile,
  busyLabel,
}: FileDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const busy = Boolean(busyFile);
  const blocked = disabled || busy;

  function handleFiles(files: FileList | null) {
    if (blocked) return;
    const file = files?.[0];
    if (file) onFile(file);
  }

  return (
    <div
      role="button"
      tabIndex={0}
      aria-disabled={blocked}
      aria-busy={busy}
      onClick={() => !blocked && inputRef.current?.click()}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !blocked) {
          e.preventDefault();
          inputRef.current?.click();
        }
      }}
      onDragOver={(e) => {
        e.preventDefault();
        if (!blocked) setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        handleFiles(e.dataTransfer.files);
      }}
      className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors ${
        busy
          ? 'cursor-progress border-brand-300 bg-brand-50/50'
          : disabled
            ? 'cursor-not-allowed border-gray-200 bg-gray-50 opacity-60'
            : dragging
              ? 'cursor-pointer border-brand-500 bg-brand-50'
              : 'cursor-pointer border-gray-300 bg-white hover:border-brand-400 hover:bg-gray-50'
      }`}
    >
      {busy && busyFile ? (
        <>
          <UploadCloud className="h-8 w-8 text-brand-500" />
          <p className="max-w-full truncate text-sm font-medium text-gray-900">{busyFile.name}</p>
          <UploadProgressBar className="max-w-xs" percent={100} phase="processing" />
          {busyLabel && <p className="text-xs text-gray-500">{busyLabel}</p>}
        </>
      ) : (
        <>
          <UploadCloud className="h-8 w-8 text-gray-400" />
          <p className="text-sm font-medium text-gray-700">
            Trageți fișierul aici sau faceți clic pentru a alege
          </p>
          {hint && <p className="text-xs text-gray-500">{hint}</p>}
        </>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          handleFiles(e.target.files);
          // Reset so re-selecting the same file fires onChange again.
          e.target.value = '';
        }}
      />
    </div>
  );
}
